import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ideasApi } from '../services/api'
import MarkdownEditor from '../components/MarkdownEditor'
import AdminSidebar from '../components/AdminSidebar'
import EmptyState from '../components/EmptyState'
import ConfirmDialog from '../components/ConfirmDialog'
import ShortcutsHelp from '../components/ShortcutsHelp'
import { useToast } from '../components/ToastContainer'
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts'
import '../styles/Elise.css'

function EliseIdeas() {
  const navigate = useNavigate()
  const toast = useToast()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [ideas, setIdeas] = useState([])
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formMessage, setFormMessage] = useState('')
  const [convertingId, setConvertingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger'
  })
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    estimated_cost: '',
    estimated_time_hours: '',
    tags: ''
  })

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      navigate('/admin/login')
    } else {
      setIsAuthenticated(true)
      loadIdeas()
    }
  }, [navigate])

  const loadIdeas = async () => {
    try {
      const response = await ideasApi.list()
      setIdeas(response.data)
    } catch (error) {
      console.error('Error loading ideas:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setFormMessage('')

    try {
      const payload = {
        ...formData,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        estimated_time_hours: formData.estimated_time_hours ? parseInt(formData.estimated_time_hours) : null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      }
      await ideasApi.create(payload)
      setFormMessage('Idea created successfully!')
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        estimated_cost: '',
        estimated_time_hours: '',
        tags: ''
      })
      loadIdeas()

      setTimeout(() => {
        setShowForm(false)
        setFormMessage('')
      }, 1500)
    } catch (error) {
      console.error('Error creating idea:', error)
      setFormMessage('Failed to create idea: ' + (error.response?.data?.detail || error.message))
    } finally {
      setSubmitting(false)
    }
  }

  const handleConvertToPlan = async (ideaId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Convert to Project Plan',
      message: 'Convert this idea to a project plan? This will create a new project plan based on this idea.',
      variant: 'primary',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false })
        setConvertingId(ideaId)

        try {
          await ideasApi.convertToPlan(ideaId)
          loadIdeas()
          toast.success('Idea converted to project plan successfully!')
        } catch (error) {
          console.error('Error converting idea:', error)
          toast.error('Failed to convert idea: ' + (error.response?.data?.detail || error.message))
        } finally {
          setConvertingId(null)
        }
      }
    })
  }

  const handleDelete = async (ideaId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Idea',
      message: 'Are you sure you want to delete this idea? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false })
        setDeletingId(ideaId)

        try {
          await ideasApi.delete(ideaId)
          loadIdeas()
          toast.success('Idea deleted successfully')
        } catch (error) {
          console.error('Error deleting idea:', error)
          toast.error('Failed to delete idea: ' + (error.response?.data?.detail || error.message))
        } finally {
          setDeletingId(null)
        }
      }
    })
  }

  // Keyboard shortcuts
  useKeyboardShortcuts({
    'ctrl+n': () => {
      if (!showForm) {
        setShowForm(true)
        toast.info('New idea form opened')
      }
    },
    '?': () => setShowShortcuts(!showShortcuts),
    'escape': () => {
      if (showForm) {
        setShowForm(false)
      }
      if (showShortcuts) {
        setShowShortcuts(false)
      }
    }
  }, [showForm, showShortcuts])

  // Shortcuts configuration for help modal
  const shortcutsConfig = {
    'Actions': [
      { keys: 'Ctrl+N', description: 'Create new idea' },
      { keys: 'Esc', description: 'Close form or modal' },
    ],
    'Navigation': [
      { keys: '?', description: 'Show keyboard shortcuts' },
    ]
  }

  if (!isAuthenticated) {
    return null
  }

  const getPriorityColor = (priority) => {
    const colors = {
      low: '#28a745',
      medium: '#ffc107',
      high: '#fd7e14',
      urgent: '#dc3545'
    }
    return colors[priority] || '#6c757d'
  }

  return (
    <div className="admin-panel">
      <AdminSidebar currentPage="ideas" />
      <div className="admin-content">
        <div className="elise-header">
          <h1>Project Ideas</h1>
          <div className="header-actions">
            <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
              {showForm ? 'Cancel' : 'New Idea'}
            </button>
          </div>
        </div>

      {showForm && (
        <div className="form-container" role="region" aria-label="New idea form">
          <h2 id="idea-form-title">Create New Idea</h2>
          <form onSubmit={handleSubmit} aria-labelledby="idea-form-title">
            <div className="form-group">
              <label htmlFor="idea-title">Title</label>
              <input
                type="text"
                id="idea-title"
                name="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                aria-required="true"
              />
            </div>

            <div className="form-group">
              <label htmlFor="idea-description">Description</label>
              <MarkdownEditor
                id="idea-description"
                value={formData.description}
                onChange={(value) => setFormData({...formData, description: value})}
                placeholder="Describe your project idea..."
                height="300px"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="idea-priority">Priority</label>
                <select
                  id="idea-priority"
                  name="priority"
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  aria-label="Select priority level"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="idea-cost">Estimated Cost ($)</label>
                <input
                  type="number"
                  id="idea-cost"
                  name="estimated_cost"
                  step="0.01"
                  value={formData.estimated_cost}
                  onChange={(e) => setFormData({...formData, estimated_cost: e.target.value})}
                  placeholder="Optional"
                  aria-label="Estimated cost in dollars"
                />
              </div>

              <div className="form-group">
                <label htmlFor="idea-time">Estimated Time (hours)</label>
                <input
                  type="number"
                  id="idea-time"
                  name="estimated_time_hours"
                  value={formData.estimated_time_hours}
                  onChange={(e) => setFormData({...formData, estimated_time_hours: e.target.value})}
                  placeholder="Optional"
                  aria-label="Estimated time in hours"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="idea-tags">Tags (comma-separated)</label>
              <input
                type="text"
                id="idea-tags"
                name="tags"
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="woodworking, electronics, 3d-printing"
                aria-describedby="tags-help"
              />
              <small id="tags-help" style={{color: 'var(--text-secondary)', fontSize: '13px', marginTop: '4px', display: 'block'}}>
                Separate multiple tags with commas
              </small>
            </div>

            {formMessage && (
              <div
                style={{
                  padding: '12px',
                  borderRadius: '6px',
                  marginTop: '16px',
                  backgroundColor: formMessage.includes('Failed') || formMessage.includes('Error')
                    ? 'var(--danger-bg)'
                    : 'var(--success-bg)',
                  color: formMessage.includes('Failed') || formMessage.includes('Error')
                    ? 'var(--danger-text)'
                    : 'var(--success-text)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {formMessage}
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Idea'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn btn-secondary" disabled={submitting}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading ideas...</div>
      ) : ideas.length === 0 ? (
        <EmptyState
          icon="💡"
          title="No Ideas Yet"
          message="Start capturing your project ideas here. Track priorities, costs, and estimated time to help you plan your next build."
          actionLabel="Create First Idea"
          onAction={() => setShowForm(true)}
        />
      ) : (
        <div className="ideas-grid">
          {ideas.map((idea) => (
            <div key={idea.id} className="idea-card">
              <div className="idea-header">
                <h3>{idea.title}</h3>
                <span
                  className="priority-badge"
                  style={{ backgroundColor: getPriorityColor(idea.priority) }}
                >
                  {idea.priority}
                </span>
              </div>

              <div className="idea-description">
                {idea.description}
              </div>

              <div className="idea-meta">
                {idea.estimated_cost && (
                  <span className="meta-item">💰 ${idea.estimated_cost}</span>
                )}
                {idea.estimated_time_hours && (
                  <span className="meta-item">⏱️ {idea.estimated_time_hours}h</span>
                )}
              </div>

              {idea.tags && idea.tags.length > 0 && (
                <div className="idea-tags">
                  {idea.tags.map((tag, idx) => (
                    <span key={idx} className="tag">{tag}</span>
                  ))}
                </div>
              )}

              <div className="idea-status">
                Status: <strong>{idea.status}</strong>
              </div>

              <div className="idea-actions">
                {idea.status === 'idea' && (
                  <button
                    onClick={() => handleConvertToPlan(idea.id)}
                    className="btn btn-primary btn-sm"
                    disabled={convertingId === idea.id || deletingId === idea.id}
                  >
                    {convertingId === idea.id ? 'Converting...' : 'Convert to Plan'}
                  </button>
                )}
                <button
                  onClick={() => handleDelete(idea.id)}
                  className="btn btn-danger btn-sm"
                  disabled={convertingId === idea.id || deletingId === idea.id}
                >
                  {deletingId === idea.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
      />

      {/* Keyboard Shortcuts Help */}
      <ShortcutsHelp
        isOpen={showShortcuts}
        onClose={() => setShowShortcuts(false)}
        shortcuts={shortcutsConfig}
      />
    </div>
  )
}

export default EliseIdeas
